import { Typography, Grid, Paper, Box, CircularProgress, Chip } from '@mui/material';
import {
  Article,
  People,
  Church,
  PhotoLibrary,
  VideoLibrary,
  Description,
  Newspaper,
  AutoStories,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { dashboardService, RecentActivity } from '../services/dashboard.service';

interface StatCardProps {
  title: string;
  count: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
}

function StatCard({ title, count, subtitle, icon, color }: StatCardProps) {
  return (
    <Paper
      sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          bgcolor: color,
          color: 'white',
          display: 'flex',
        }}
      >
        {icon}
      </Box>
      <div>
        <Typography variant="h4" component="div">
          {count}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {subtitle && (
          <Chip size="small" label={subtitle} sx={{ mt: 1 }} />
        )}
      </div>
    </Paper>
  );
}

function ActivityItem({ activity }: { activity: RecentActivity }) {
  const actionColors: Record<string, string> = {
    create: 'success',
    update: 'primary',
    delete: 'error',
  };

  const actionLabels: Record<string, string> = {
    create: 'Создание',
    update: 'Изменение',
    delete: 'Удаление',
  };

  return (
    <Box sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          size="small"
          color={actionColors[activity.action] as 'success' | 'primary' | 'error' | 'default'}
          label={actionLabels[activity.action] || activity.action}
        />
        <Typography variant="body2">
          {activity.entityType || 'Система'}
        </Typography>
        {activity.user && (
          <Typography variant="caption" color="text.secondary">
            — {activity.user}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {new Date(activity.timestamp).toLocaleString('ru-RU')}
      </Typography>
    </Box>
  );
}

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: dashboardService.getStats,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: activity, isLoading: activityLoading } = useQuery({
    queryKey: ['dashboard-activity'],
    queryFn: () => dashboardService.getRecentActivity(10),
    refetchInterval: 30000,
  });

  if (statsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const statCards: StatCardProps[] = [
    { title: 'Новости', count: stats?.news?.total || 0, subtitle: `${stats?.news?.published || 0} опубликовано`, icon: <Article />, color: '#4caf50' },
    { title: 'Храмы', count: stats?.temples?.total || 0, icon: <Church />, color: '#9c27b0' },
    { title: 'Духовенство', count: stats?.clergy?.total || 0, icon: <People />, color: '#2196f3' },
    { title: 'Видео', count: stats?.videos?.total || 0, icon: <VideoLibrary />, color: '#f44336' },
    { title: 'Документы', count: stats?.documents?.total || 0, icon: <Description />, color: '#ff9800' },
    { title: 'Журналы', count: stats?.newspapers?.total || 0, icon: <Newspaper />, color: '#795548' },
    { title: 'Некрополь', count: stats?.necropolis?.total || 0, icon: <AutoStories />, color: '#607d8b' },
    { title: 'Галереи', count: stats?.galleries?.total || 0, icon: <PhotoLibrary />, color: '#00bcd4' },
  ];

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontFamily: 'Noto Serif' }}>
        Главная панель
      </Typography>

      <Grid container spacing={3}>
        {statCards.map((stat) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={stat.title}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2, fontFamily: 'Noto Serif' }}>
        Последние действия
      </Typography>
      <Paper sx={{ p: 2 }}>
        {activityLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : activity && activity.length > 0 ? (
          <Box>
            {activity.map((item) => (
              <ActivityItem key={item.id} activity={item} />
            ))}
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Нет записей о последних действиях
          </Typography>
        )}
      </Paper>
    </div>
  );
}